import { useState } from "react";
import { api, INVENTORY_LOCATIONS, inventoryLocationLabel, type Driver, type InventoryItem } from "../api";
import { goBack, pushScreen } from "../backstack";
import BackHeader from "../components/BackHeader";
import PinDialog from "../components/PinDialog";
import { formatDue } from "../scheduler/dates";
import { prepareImage } from "../scheduler/photos";
import ItemForm from "./ItemForm";

const LABEL = "px-1 text-[12.5px] font-semibold text-[#3d2930]";

export function itemTitle(item: InventoryItem) {
  return item.description || [item.brand, item.model].filter(Boolean).join(" ") || item.itemType || "No description yet";
}

export default function ItemDetail({
  item,
  driver,
  onChange,
  onDeleted,
  onSearchTag,
  onBack,
}: {
  item: InventoryItem;
  driver: Driver;
  onChange: (item: InventoryItem) => void;
  onDeleted: () => void;
  onSearchTag: (tag: string) => void;
  onBack: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [shown, setShown] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [confirmPhoto, setConfirmPhoto] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const photoId = item.photoIds[Math.min(shown, item.photoIds.length - 1)];

  const run = async (label: string, action: () => Promise<void>) => {
    if (busy) return;
    setBusy(label);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(null);
    }
  };

  const addPhotos = (files: File[]) => {
    if (!files.length) return;
    run("Uploading…", async () => {
      const ids = [...item.photoIds];
      for (const file of files) {
        const full = await prepareImage(file);
        const thumb = await prepareImage(full, 480, 0.72);
        ids.push((await api.uploadInventoryPhoto(item.id, full, thumb)).id);
        onChange({ ...item, photoIds: [...ids] });
      }
      setShown(ids.length - 1);
    });
  };

  const deleteShownPhoto = () =>
    run("Deleting…", async () => {
      if (!photoId) return;
      await api.deleteInventoryPhoto(photoId);
      goBack(); // closes the lightbox through its history entry
      setShown(0);
      onChange({ ...item, photoIds: item.photoIds.filter((p) => p !== photoId) });
    });

  if (editing) {
    return (
      <ItemForm
        driver={driver}
        initial={item}
        onSaved={(updated) => {
          onChange({ ...updated, photoIds: item.photoIds });
          goBack();
        }}
        onCancel={goBack}
      />
    );
  }

  const facts = [
    [
      "Place",
      `${INVENTORY_LOCATIONS.find((l) => l.value === item.location)?.store ? "Store " : ""}${inventoryLocationLabel(item.location)}`,
    ],
    ["Type", item.itemType],
    ["Brand", item.brand],
    ["Model", item.model],
  ].filter(([, value]) => value);

  return (
    <div className="flex flex-1 flex-col gap-4">
      <BackHeader
        title="Item"
        onBack={onBack}
        action={
          <button
            onClick={() => {
              setEditing(true);
              pushScreen(() => setEditing(false));
            }}
            className="shrink-0 flex h-10 items-center rounded-[12px] border border-[#efdce0] bg-white px-3.5 text-sm font-bold text-[#3a1c25] active:scale-95"
          >
            Edit
          </button>
        }
      />

      <div className="pop-in flex flex-col gap-4">
        {photoId ? (
          <button
            onClick={() => {
              setLightbox(true);
              pushScreen(() => setLightbox(false));
            }}
            className="overflow-hidden rounded-[18px] bg-white border border-line-soft shadow-card"
          >
            <img src={api.inventoryPhotoUrl(photoId)} alt="" className="max-h-[55dvh] w-full object-cover" />
          </button>
        ) : (
          <div className="flex h-40 items-center justify-center rounded-[18px] bg-white text-sm font-semibold text-ink/40 border border-line">
            No photo yet
          </div>
        )}

        {item.photoIds.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {item.photoIds.map((id, i) => (
              <button
                key={id}
                onClick={() => setShown(i)}
                className={`size-16 shrink-0 overflow-hidden rounded-xl bg-white shadow-sm ${
                  i === shown ? "ring-2 ring-hot" : ""
                }`}
              >
                <img src={api.inventoryPhotoUrl(id, true)} alt="" loading="lazy" className="size-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-3 rounded-[18px] bg-white p-5 border border-line-soft shadow-card">
          <h2 className={`text-lg font-bold leading-snug ${item.description ? "" : "text-ink/40"}`}>{itemTitle(item)}</h2>
          {facts.length > 0 && (
            <dl className="flex flex-col gap-1 text-sm">
              {facts.map(([label, value]) => (
                <div key={label} className="flex gap-3">
                  <dt className="w-14 shrink-0 font-semibold text-ink/50">{label}</dt>
                  <dd className="min-w-0 font-semibold">{value}</dd>
                </div>
              ))}
            </dl>
          )}
          {item.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {item.tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => onSearchTag(tag)}
                  className="rounded-full bg-lavender px-3 py-1 text-sm font-bold active:scale-95"
                >
                  {tag}
                </button>
              ))}
            </div>
          )}
          <p className="text-xs font-semibold text-ink/50">
            Added {formatDue(item.createdAt)}
            {item.createdByName && ` by ${item.createdByName}`}
          </p>
        </div>

        <section className="flex flex-col gap-2">
          <span className={LABEL}>Photos</span>
          <div className="flex gap-2">
            <label className="flex h-12 flex-1 cursor-pointer items-center justify-center rounded-[14px] bg-blush text-sm font-bold text-rose-ink active:scale-95">
              📷 Take photo
              <input
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                disabled={busy !== null}
                onChange={(e) => {
                  addPhotos([...(e.target.files ?? [])]);
                  e.target.value = "";
                }}
              />
            </label>
            <label className="flex h-12 flex-1 cursor-pointer items-center justify-center rounded-[14px] bg-white text-sm font-bold active:scale-95 border border-line">
              Add from library
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                disabled={busy !== null}
                onChange={(e) => {
                  addPhotos([...(e.target.files ?? [])]);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
          {busy && <p className="text-center text-sm font-semibold text-ink/50">{busy}</p>}
        </section>

        {error && <p className="text-center text-sm font-semibold text-danger">{error}</p>}

        <button
          onClick={() => setConfirmDelete(true)}
          disabled={busy !== null}
          className="mx-auto mt-2 px-4 py-2 text-sm font-bold text-danger"
        >
          Delete item
        </button>
      </div>

      {lightbox && photoId && (
        <div className="fixed inset-0 z-50 flex flex-col bg-ink" onClick={goBack}>
          <img src={api.inventoryPhotoUrl(photoId)} alt="" className="min-h-0 flex-1 object-contain" />
          <div className="flex gap-2 p-4" onClick={(e) => e.stopPropagation()}>
            <button onClick={goBack} className="h-12 flex-1 rounded-[14px] bg-white font-bold">
              Close
            </button>
            <button
              onClick={() => setConfirmPhoto(true)}
              className="h-12 flex-1 rounded-[14px] bg-danger font-bold text-white"
            >
              Delete photo
            </button>
          </div>
        </div>
      )}

      {confirmPhoto && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/40 px-6" onClick={() => setConfirmPhoto(false)}>
          <div
            className="pop-in flex w-full max-w-xs flex-col gap-4 rounded-[20px] bg-white p-6 shadow-pop"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-[19px] font-bold tracking-[-0.02em]">Delete this photo?</h2>
            <div className="flex gap-2">
              <button onClick={() => setConfirmPhoto(false)} className="h-12 flex-1 rounded-[14px] bg-white font-bold border border-line text-[#3a1c25]">
                Cancel
              </button>
              <button
                onClick={() => {
                  setConfirmPhoto(false);
                  deleteShownPhoto();
                }}
                className="h-12 flex-1 rounded-[14px] bg-danger font-bold text-white"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <PinDialog
          title="Delete this item?"
          detail={`"${itemTitle(item)}" will be hidden for everyone. An admin can still restore it. Enter the PIN to delete.`}
          onConfirm={async (pin) => {
            await api.deleteInventoryItem(item.id, pin, driver.id);
            onDeleted();
          }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
